import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ScoreGauge } from "@/components/ScoreVisuals";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";

const COLOR_HEX = { groen: "#6B8E74", geel: "#E0B84B", oranje: "#D98A4E", rood: "#C86B5E" };

export default function AdminReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("wm_token")) { navigate("/admin/login"); return; }
    api.get(`/admin/participants/${id}`).then((r) => setD(r.data)).catch(() => navigate("/admin"));
  }, [id, navigate]);

  if (!d) return <div className="min-h-screen flex items-center justify-center bg-[#FBF7F0]"><Loader2 className="animate-spin text-[#6B8E74]" size={32} /></div>;

  const answersByDomain = (dom) => (d.questions || []).filter((q) => q.domain === dom);

  return (
    <div className="min-h-screen bg-[#FBF7F0] py-8 px-5">
      <div className="max-w-3xl mx-auto space-y-8">
        <button onClick={() => navigate("/admin")} data-testid="report-back" className="inline-flex items-center gap-1 text-sm text-[#5B6B61] hover:text-[#3A4A40]"><ArrowLeft size={16} /> Terug naar backoffice</button>

        <div className="bg-white rounded-[28px] p-6 sm:p-10 shadow-[0_16px_50px_rgba(58,74,64,0.08)] border border-[#EDE7DC] text-center">
          <p className="text-sm text-[#8FA38C] uppercase tracking-widest mb-1">Volledig welzijnsrapport</p>
          <h1 className="text-2xl sm:text-3xl text-[#3A4A40] mb-4">{d.voornaam} {d.familienaam}</h1>
          <ScoreGauge score={d.total_score} category={d.category} />
          <h2 className="text-xl text-[#3A4A40] mt-4">{d.category}</h2>
          <p className="text-sm text-[#8FA38C] mt-1">{d.email} • {d.telefoon} • {d.gemeente} • {(d.created_at || "").slice(0, 10)}</p>
          {d.interpretation && <p className="text-[#5B6B61] leading-relaxed mt-5 max-w-xl mx-auto">{d.interpretation}</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {(d.category_details || []).map((c) => {
            const hex = COLOR_HEX[c.color] || "#6B8E74";
            return (
              <div key={c.key} className="bg-white rounded-3xl p-5 border border-[#EDE7DC]">
                <div className="flex justify-between items-baseline mb-2"><span className="text-[#3A4A40] font-medium">{c.label}</span><span className="font-serif" style={{ color: hex }}>{c.score}/100</span></div>
                <div className="h-2.5 rounded-full bg-[#EDEAE1] overflow-hidden mb-2"><div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: hex }} /></div>
                <p className="text-sm text-[#5B6B61]">{c.explanation}</p>
              </div>
            );
          })}
        </div>

        {d.tips?.length > 0 && (
          <div className="bg-[#F1EEE5] rounded-[28px] p-6 sm:p-8 border border-[#E5DFD2]">
            <div className="flex items-center gap-2 mb-4"><span className="bg-[#6B8E74] text-white rounded-full p-2"><Sparkles size={18} /></span><h3 className="text-xl text-[#3A4A40]">Persoonlijke adviezen</h3></div>
            <ul className="space-y-2">{d.tips.map((t, i) => <li key={i} className="flex gap-3 text-sm text-[#3A4A40]"><span className="text-[#6B8E74] font-semibold">{i + 1}.</span> {t}</li>)}</ul>
          </div>
        )}

        {/* All answers */}
        <div className="bg-white rounded-[28px] p-6 sm:p-8 border border-[#EDE7DC]">
          <h3 className="text-xl text-[#3A4A40] mb-4">Alle antwoorden</h3>
          {Object.entries(d.domains || {}).map(([dk, dl]) => (
            <div key={dk} className="mb-5">
              <p className="text-sm font-semibold text-[#6B8E74] mb-2">{dl} <span className="text-[#8FA38C] font-normal">({(d.subscores || {})[dk] ?? 0}/100)</span></p>
              <div className="space-y-1">
                {answersByDomain(dk).map((q) => {
                  const v = (d.answers || {})[q.id];
                  return <div key={q.id} className="flex justify-between gap-3 text-sm border-b border-[#F3F0E8] pb-1"><span className="text-[#5B6B61]">{q.text}</span><span className="text-[#3A4A40] font-medium whitespace-nowrap">{v ? `${v} — ${(d.scale || {})[String(v)] || ""}` : "—"}</span></div>;
                })}
              </div>
            </div>
          ))}
          {d.levensfase && <p className="text-sm text-[#5B6B61] mt-2">Levensfase: <span className="text-[#3A4A40]">{d.levensfase}</span></p>}
        </div>
      </div>
    </div>
  );
}
